import { useFormik } from 'formik';
import { Filter, RefreshCw, Search } from 'lucide-react';
import styled from 'styled-components';

import { Button } from '../../../../../../components/atoms/button/button';
import { DateInput } from '../../../../../../components/atoms/date-input/date-input';
import { Input } from '../../../../../../components/atoms/input/input';
import { Select } from '../../../../../../components/atoms/select/select';
import {
  ClienteVendasFilters,
  STATUS_VENDA_LABEL,
  StatusVenda,
} from '../../../../../../types/cliente-venda';

interface Props {
  initialValues: ClienteVendasFilters;
  onApply: (values: ClienteVendasFilters) => void;
  onClear: () => void;
}

const Wrapper = styled.section`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  background: ${({ theme }) => theme.colors.bgSurface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: 16px;

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const Actions = styled.div`
  grid-column: 1 / -1;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const statusOptions = [
  { label: 'Todos os status', value: '' },
  ...Object.entries(STATUS_VENDA_LABEL).map(([value, label]) => ({
    label,
    value,
  })),
];

const upsellOptions = [
  { label: 'Todas', value: '' },
  { label: 'Apenas upsells', value: 'true' },
  { label: 'Apenas vendas principais', value: 'false' },
];

const moedaOptions = [
  { label: 'Todas as moedas', value: '' },
  { label: 'BRL', value: '1' },
  { label: 'USD', value: '2' },
  { label: 'EUR', value: '3' },
];

const toBoolean = (raw: string): boolean | '' =>
  raw === '' ? '' : raw === 'true';

const toNumberOrEmpty = (raw: string): number | '' =>
  raw === '' ? '' : Number(raw);

export const VendasFilters = ({ initialValues, onApply, onClear }: Props) => {
  const formik = useFormik<ClienteVendasFilters>({
    initialValues,
    enableReinitialize: true,
    onSubmit: (values) => onApply(values),
  });

  return (
    <Wrapper
      as="form"
      onSubmit={formik.handleSubmit}
      aria-label="Filtros de vendas do cliente"
    >
      <Field>
        Produto (nome)
        <Input
          name="produtoNome"
          placeholder="Ex: Curso Avançado"
          value={formik.values.produtoNome ?? ''}
          onChange={formik.handleChange}
          startIcon={<Search size={16} />}
        />
      </Field>

      <Field>
        Comprador (email)
        <Input
          name="compradorEmail"
          type="email"
          placeholder="comprador@email"
          value={formik.values.compradorEmail ?? ''}
          onChange={formik.handleChange}
        />
      </Field>

      <Field>
        Status
        <Select
          options={statusOptions}
          name="status"
          value={(formik.values.status ?? '').toString()}
          onChange={(e) =>
            formik.setFieldValue(
              'status',
              e.target.value === ''
                ? ''
                : (Number(e.target.value) as StatusVenda),
            )
          }
        />
      </Field>

      <Field>
        Upsell
        <Select
          options={upsellOptions}
          name="upsell"
          value={
            formik.values.upsell === undefined || formik.values.upsell === ''
              ? ''
              : formik.values.upsell
                ? 'true'
                : 'false'
          }
          onChange={(e) => formik.setFieldValue('upsell', toBoolean(e.target.value))}
        />
      </Field>

      <Field>
        Moeda
        <Select
          options={moedaOptions}
          name="moeda"
          value={(formik.values.moeda ?? '').toString()}
          onChange={(e) =>
            formik.setFieldValue('moeda', toNumberOrEmpty(e.target.value))
          }
        />
      </Field>

      <Field>
        Comprador (nome)
        <Input
          name="compradorNome"
          placeholder="Nome ou sobrenome"
          value={formik.values.compradorNome ?? ''}
          onChange={formik.handleChange}
        />
      </Field>

      <Field>
        Data início
        <DateInput
          name="dataInicio"
          value={formik.values.dataInicio ?? ''}
          onChange={(e) =>
            formik.setFieldValue(
              'dataInicio',
              e.target.value
                ? new Date(e.target.value).toISOString()
                : '',
            )
          }
        />
      </Field>

      <Field>
        Data fim
        <DateInput
          name="dataFim"
          value={formik.values.dataFim ?? ''}
          onChange={(e) =>
            formik.setFieldValue(
              'dataFim',
              e.target.value
                ? new Date(e.target.value).toISOString()
                : '',
            )
          }
        />
      </Field>

      <Field>
        Valor mínimo
        <Input
          name="valorMinimo"
          type="number"
          min={0}
          step="0.01"
          inputMode="decimal"
          placeholder="0,00"
          value={
            formik.values.valorMinimo === '' ||
            formik.values.valorMinimo === undefined
              ? ''
              : String(formik.values.valorMinimo)
          }
          onChange={(e) =>
            formik.setFieldValue(
              'valorMinimo',
              toNumberOrEmpty(e.target.value),
            )
          }
        />
      </Field>

      <Field>
        Valor máximo
        <Input
          name="valorMaximo"
          type="number"
          min={0}
          step="0.01"
          inputMode="decimal"
          placeholder="0,00"
          value={
            formik.values.valorMaximo === '' ||
            formik.values.valorMaximo === undefined
              ? ''
              : String(formik.values.valorMaximo)
          }
          onChange={(e) =>
            formik.setFieldValue(
              'valorMaximo',
              toNumberOrEmpty(e.target.value),
            )
          }
        />
      </Field>

      <Actions>
        <Button
          variant="ghost"
          type="button"
          startIcon={<RefreshCw size={14} />}
          onClick={() => {
            formik.resetForm({ values: {} });
            onClear();
          }}
        >
          Limpar
        </Button>
        <Button
          variant="primary"
          type="submit"
          startIcon={<Filter size={14} />}
        >
          Aplicar filtros
        </Button>
      </Actions>
    </Wrapper>
  );
};
